/**
 * View builders shared by the Character sheet (./engine.ts) and the Familiar sheet
 * (./familiar.ts): modifiers, notes, check extras, IWR and statistics with their Override.
 */
import type { Override } from "../character/model";
import type { CheckExtrasView, DamageDiceView, IWRView, ModifierView, NoteView, SpeedView, StatisticView } from "./engine";
import type { DamageDice } from "./damage-dice";
import { testPredicate } from "./predicate";
import type { DerivedSpeed } from "./movement";
import { iwrLabel, type IWRData } from "./iwr";
import type { Modifier } from "./modifiers";
import type { EngineState } from "./state";
import type { CheckDegreeAdjustment, CheckEphemeralEffect, CheckExtras, CheckNote, CheckRollTwice, CheckSubstitution, DerivedStatistic } from "./statistics";

/** Overrides by statistic key, recording which ones a sheet used (the rest are dangling). */
export function createOverrideLookup(overrides: Override[]): { overrideFor: (key: string) => Override | null; used: Set<string> } {
  const map = new Map(overrides.map((o) => [o.statistic, o]));
  const used = new Set<string>();
  return {
    overrideFor: (key) => {
      const o = map.get(key) ?? null;
      if (o) used.add(key);
      return o;
    },
    used,
  };
}

/** A speed as the sheet shows it, with its Override. */
export function speedView(speed: DerivedSpeed, overrideKey: string, override: Override | null, traced?: number | undefined): SpeedView {
  const derivedValue = typeof traced === "number" ? traced : speed.value;
  return {
    type: speed.type,
    label: speed.label,
    base: speed.base,
    source: speed.source,
    derived: derivedValue,
    override,
    value: override?.value ?? derivedValue,
    modifiers: speed.modifiers.map(modifierView),
    breakdown: speed.breakdown,
    derivedFrom: speed.derivedFrom,
    overrideKey,
  };
}

/** A statistic as the sheet shows it: the Override's value when one is set, the derived value beside it. */
export function statisticView(statistic: DerivedStatistic, overrideKey: string, override: Override | null, options: { useDC?: boolean; traced?: number | undefined } = {}): StatisticView {
  const useDC = options.useDC ?? false;
  const base = useDC ? statistic.dc.value : statistic.check.mod;
  const derivedValue = typeof options.traced === "number" ? options.traced : base;
  return {
    slug: statistic.slug,
    label: statistic.label,
    attribute: statistic.attribute,
    rank: statistic.rank,
    lore: statistic.lore,
    derived: derivedValue,
    override,
    value: override?.value ?? derivedValue,
    modifiers: (useDC ? statistic.dc.modifiers : statistic.check.modifiers).map(modifierView),
    breakdown: useDC ? statistic.dc.breakdown : statistic.check.breakdown,
    dc: statistic.dc.value,
    dcModifiers: statistic.dc.modifiers.map(modifierView),
    dcBreakdown: statistic.dc.breakdown,
    overrideKey,
    ...extrasView(statistic.check),
  };
}

export function modifierView(m: Modifier): ModifierView {
  return {
    slug: m.slug,
    label: m.label,
    type: m.type,
    value: m.value,
    enabled: m.enabled,
    ignored: m.ignored,
    hideIfDisabled: m.hideIfDisabled,
    source: m.source,
    sourceName: m.sourceName,
    ability: m.ability,
    predicate: m.predicate,
    adjustedBy: [...m.adjustedBy],
    damageType: m.damageType,
    damageCategory: m.damageCategory,
    critical: m.critical,
    removeAfterRoll: m.removeAfterRoll === true || (m.removeAfterRoll === "if-enabled" && m.enabled) || (Array.isArray(m.removeAfterRoll) && m.removeAfterRoll.length > 0 && m.enabled && testPredicate(m.removeAfterRoll, new Set(m.parentOptions))),
  };
}

export function damageDiceView(d: DamageDice): DamageDiceView {
  return { slug: d.slug, label: d.label, diceNumber: d.diceNumber, dieSize: d.dieSize, damageType: d.damageType, category: d.category, critical: d.critical, enabled: d.enabled, hideIfDisabled: d.hideIfDisabled, override: d.override ? { ...d.override } : null, tags: [...d.tags], source: d.source, sourceName: d.sourceName };
}

export function noteView(n: CheckNote): NoteView {
  return { selector: n.selector, title: n.title, text: n.text, predicate: n.predicate, outcome: [...n.outcome], visibility: n.visibility, enabled: n.enabled, postRoll: n.postRoll, source: n.source, sourceName: n.sourceName };
}

export function extrasView(check: CheckExtras): CheckExtrasView {
  return {
    notes: check.notes.map(noteView),
    degreeAdjustments: check.degreeAdjustments.map((a: CheckDegreeAdjustment) => ({ adjustments: a.adjustments, predicate: a.predicate, enabled: a.enabled, postRoll: a.postRoll, source: a.source, sourceName: a.sourceName })),
    rollTwice: check.rollTwice,
    rollTwiceSources: check.rollTwiceSources.map((rt: CheckRollTwice) => ({ keep: rt.keep, predicate: rt.predicate, enabled: rt.enabled, removeAfterRoll: rt.removeAfterRoll, source: rt.source, sourceName: rt.sourceName })),
    substitutions: check.substitutions.map((s: CheckSubstitution) => ({ slug: s.slug, label: s.label, value: s.value, required: s.required, effectType: s.effectType, predicate: s.predicate, enabled: s.enabled, removeAfterRoll: s.removeAfterRoll, source: s.source, sourceName: s.sourceName })),
    ephemeralEffects: check.ephemeralEffects.map((e: CheckEphemeralEffect) => ({ uuid: e.uuid, name: e.name, affects: e.affects, predicate: e.predicate, enabled: e.enabled, source: e.source, sourceName: e.sourceName })),
  };
}

export function iwrView(state: EngineState, iwr: IWRData): IWRView {
  return { kind: iwr.kind, type: iwr.type, value: iwr.value, exceptions: structuredClone(iwr.exceptions), doubleVs: structuredClone(iwr.doubleVs), definition: iwr.definition ? structuredClone(iwr.definition) : null, label: iwrLabel(state, iwr), source: iwr.source, sourceName: iwr.sourceName, applyOnce: iwr.applyOnce, manual: iwr.manual ? { ...iwr.manual } : null };
}
